import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { NavLink } from "react-router-dom";
import { motion } from "framer-motion";

import Dot from "../../../public/images/Dot";
import "./cards.scss";

const ITEMS = [
  { label: "VoIP", to: "/voip" },
  { label: "Apps", to: "/apps" },
  { label: "Cloud", to: "/cloud" },
  { label: "AI", to: "/im" },
];

export default function Cards() {
  const [activeIdx, setActiveIdx] = useState(-1);
  const [pinnedIdx, setPinnedIdx] = useState(-1);

  useEffect(() => {
    const baseStart = 150;
    const perCardGap = 900;
    const pinDelay = 1350;
    const timers = [];

    ITEMS.forEach((_, idx) => {
      timers.push(
        setTimeout(() => setActiveIdx(idx), baseStart + idx * perCardGap),
      );
      timers.push(
        setTimeout(
          () => setPinnedIdx(idx),
          baseStart + idx * perCardGap + pinDelay,
        ),
      );
    });

    return () => timers.forEach(clearTimeout);
  }, []);

  const easeSnap = [0.2, 0.9, 0.2, 1];

  const isVisible = (idx) => idx <= activeIdx;
  const isPinned = (idx) => idx <= pinnedIdx;

  const finalAngles = [-4, 0, 0, -2];

  const pinnedPose = (idx) => {
    if (idx === 0)
      return { rotateX: 14, rotateZ: finalAngles[idx], z: 0, scale: 1 };
    if (idx === 3)
      return { rotateX: -1.6, rotateZ: finalAngles[idx], z: 0, scale: 1 };
    return { rotateX: 0, rotateZ: finalAngles[idx], z: 0, scale: 1 };
  };

  const hoverPose = (idx) => {
    if (idx === 2) return { rotateZ: -8 };
    if (idx === 3) return { rotateX: -1.2, rotateZ: -8, scale: 1.002 };
    return { rotateZ: 16 };
  };

  const wobbleOnceByCard = (idx) => {
    const amps = [22, 20, 18, 21];
    const a = amps[idx] ?? 20;
    const end = finalAngles[idx];

    return {
      rotateZ: [-a, a, -(a * 0.7), a * 0.5, -(a * 0.3), a * 0.18, end],
      transition: {
        duration: 1.25,
        ease: "easeInOut",
        times: [0, 0.18, 0.35, 0.52, 0.7, 0.86, 1],
      },
    };
  };

  const cardEnter = useMemo(
    () => ({
      hidden: { opacity: 0, y: 90, scale: 1.07 },
      visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.65, ease: easeSnap },
      },
    }),
    [],
  );

  const swingEnter = useMemo(
    () => ({
      hidden: { z: -170, scale: 1.12 },
      visible: {
        z: 0,
        scale: 1,
        transition: { duration: 0.45, ease: easeSnap },
      },
    }),
    [],
  );

  const dotVariants = useMemo(
    () => ({
      hidden: { opacity: 0, z: -180, scale: 1.4 },
      visible: {
        opacity: 1,
        z: 0,
        scale: 1,
        transition: { duration: 0.35, ease: "easeOut" },
      },
    }),
    [],
  );

  const dotPop = {
    z: [0, 40, 0],
    scale: [1, 1.18, 1],
    transition: { duration: 0.3, times: [0, 0.55, 1], ease: "easeOut" },
  };

  return (
    <div className="home__cards">
      {ITEMS.map((item, idx) => {
        const visible = isVisible(idx);
        const pinned = isPinned(idx);

        return (
          <motion.div
            key={item.label}
            className={clsx("home__cards__card", `card-${idx + 1}`)}
            initial="hidden"
            animate={visible ? "visible" : "hidden"}
            variants={cardEnter}
          >
            <motion.div
              className="home__cards__card__swing"
              style={{ opacity: 1 }}
              initial="hidden"
              animate={
                visible
                  ? pinned
                    ? pinnedPose(idx)
                    : {
                        ...swingEnter.visible,
                        ...wobbleOnceByCard(idx),
                      }
                  : swingEnter.hidden
              }
              whileHover={pinned ? hoverPose(idx) : undefined}
              transition={{ ease: easeSnap }}
            >
              <div className="home__cards__card__content">
                <NavLink to={item.to} className="home__cards__card__link">
                  {item.label}
                </NavLink>
              </div>
            </motion.div>

            <motion.div
              className="home__cards__card__dot"
              initial="hidden"
              animate={pinned ? "visible" : "hidden"}
              variants={dotVariants}
            >
              <motion.div animate={pinned ? dotPop : undefined}>
                <Dot />
              </motion.div>
            </motion.div>
          </motion.div>
        );
      })}
    </div>
  );
}
